import { useState, ChangeEvent, useEffect } from "react";
import { useGetModulesQuery } from "../../../../hooks/AdminManagement/useGetModulesQuery";
import { useCreateRoleMutation } from "../../../../hooks/AdminManagement/useCreateRoleMutation";
import { useGetRolesQuery } from "hooks/AdminManagement/useGetRolesQuery";
import { useParams } from "react-router-dom";
import PermissionsEditor from "./PermissionsEditor";
import Button from "Components/Button";
import { useUpateRoleMutation } from "hooks/AdminManagement/useUpateRoleMutation";

export interface Modules {
  name: string;
  description: string;
  key: string;
  id: string;
  colorCode: string;
}

interface PermissionItem {
  create: boolean | null;
  read: boolean | null;
  update: boolean | null;
  delete: boolean | null;
}

interface Payload {
  name: string;
  description: string;
  modulePermissions: {
    moduleId: string;
    permissions: PermissionItem;
  }[];
}

const initialState: Payload = {
  name: "",
  description: "",
  modulePermissions: [],
};

const EditRoleForm = () => {
  const { id } = useParams();
  const { data: modules } = useGetModulesQuery();
  const { data: role } = useGetRolesQuery(id);
  const [payload, setPayload] = useState<Payload>(initialState);
  const { mutate, isPending } = useUpateRoleMutation({
    payload,
  });

  // Populate form from role
  useEffect(() => {
    if (role?.data) {
      setPayload({
        name: role.data.name || "",
        description: role.data.description || "",
        modulePermissions: Array.isArray(role.data.modulePermissions)
          ? role.data.modulePermissions
          : [],
      });
    }
  }, [role]);

  // Sync with available modules and role permission structure
  useEffect(() => {
    const modulesList = modules?.data ?? [];
    const rolePerms = role?.data?.permission ?? [];

    if (role?.data && modulesList.length && rolePerms.length) {
      const mappedPermissions = modulesList
        .map((module: Modules) => {
          const found = rolePerms.find(
            (perm: any) => perm?.module?.id === module?.id
          );

          if (!found) return null;

          const permissions: PermissionItem = {
            create: found.create ?? false,
            read: found.read ?? false,
            update: found.update ?? false,
            delete: found.delete ?? false,
          };

          return {
            moduleId: module.id,
            permissions,
          };
        })
        .filter(Boolean) as Payload["modulePermissions"];

      setPayload((prev) => ({
        ...prev,
        modulePermissions: mappedPermissions,
      }));
    }
  }, [role, modules]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    mutate(id || "");
  };

  return (
    <div className="px-4">
      <div className="max-w-[800px] mx-auto mt-[30px] border border-[#eeeeee] rounded-[10px]">
        <div className="border-b border-[#eeeeee]">
          <h3 className="p-[15px]">Edit Role</h3>
        </div>
        <div>
          <div className="p-[20px]">
            <label htmlFor="name" className="mt-[20px] block">
              Role Name
            </label>
            <input
              name="name"
              type="text"
              placeholder="Role"
              className="px-[15px] mb-[21px] py-[20px] border border-[#eeeeee] block mt-[13px] w-full rounded-md"
              value={payload.name}
              onChange={handleInputChange}
            />

            <label htmlFor="description" className="mt-[20px] block">
              Role Description
            </label>
            <textarea
              name="description"
              placeholder="Description"
              className="h-[170px] resize-none px-[15px] py-[20px] border border-[#eeeeee] block mt-[13px] w-full rounded-md"
              value={payload.description}
              onChange={handleInputChange}
            />
          </div>

          <div className="border-t border-[#eeeeee] px-[20px] py-[10px] text-[#a8a8a8]">
            Custom Permissions
          </div>

          <PermissionsEditor
            modules={modules?.data ?? []}
            modulePermissions={payload.modulePermissions}
            onUpdate={(updatedPermissions) =>
              setPayload((prev) => ({
                ...prev,
                modulePermissions: updatedPermissions,
              }))
            }
          />

          <div className="m-[20px] ">
            <Button
              onClick={handleSubmit}
              disabled={!payload.name || !payload.description}
              isLoading={isPending}
              label="Submit"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRoleForm;
